import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import {
  ProductCardDateValuesType,
  ProductCardIsinToNameType,
} from "types/AssistantTypes";
import styles from "./rendimentiChartCompare.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";
import { formatItalianNumber } from "utils/formatUtils";

type RendimentiChartProps = {
  andamento: ProductCardDateValuesType[];
  performanceProducts: string[];
  isinToName: ProductCardIsinToNameType;
  useYAxisPercentage?: boolean;
};

const RendimentiChartCompare: React.FC<RendimentiChartProps> = ({
  andamento,
  performanceProducts,
  isinToName,
  useYAxisPercentage = false,
}) => {
  const { i18n } = useTranslation();
  const [selectedProducts, setSelectedProducts] =
    useState<string[]>(performanceProducts);

  // Format data for the chart
  const formattedData = andamento.map((item) => {
    const dateObj = new Date(item.date);
    const formattedDate = formatLocalizedDate(
      dateObj,
      i18n.language,
      "MM/yyyy",
    );

    // Create an object with the formatted date
    const dataPoint: any = {
      originalDate: item.date,
      date: formattedDate,
    };

    // Add each product's value to the data point
    performanceProducts.forEach((isin, index) => {
      dataPoint[isin] = item.values[index];
    });

    return dataPoint;
  });

  const filteredData = formattedData;

  // Calculate domain for Y-axis based on data - IMPROVED APPROACH
  const allValues = filteredData
    .flatMap((item) => performanceProducts.map((isin) => item[isin]))
    .filter((value) => value !== undefined);

  // Find the actual min and max values in the data
  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);

  // Calculate tick step based on the data range
  const valueDiff = dataMax - dataMin;
  let tickStep = valueDiff < 60 ? 10 : 25;
  tickStep = valueDiff < 40 ? 5 : tickStep;
  tickStep = valueDiff < 20 ? 2 : tickStep;
  tickStep = valueDiff < 10 ? 1 : tickStep;

  // Round min and max to nice values based on tickStep
  // For min: round down to the nearest tickStep multiple
  const minValue = Math.floor(dataMin / tickStep) * tickStep;
  // For max: round up to the nearest tickStep multiple
  const maxValue = Math.ceil(dataMax / tickStep) * tickStep;

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };

  const lineColors = ["#103976", "#009688", "#5CA8E6", "#34A1B2", "#2C70D4"];

  if (andamento.length === 0 || performanceProducts.length === 0) {
    return null;
  }

  const toggleProductSelection = (isin: string) => {
    setSelectedProducts((prev) =>
      prev.includes(isin) ? prev.filter((p) => p !== isin) : [...prev, isin],
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      const formattedDate = formatLocalizedDate(
        new Date(entry.payload.originalDate),
        i18n.language,
        "dd/MM/yyyy",
      );

      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p
            className={styles.tooltipLabel}
            style={{ margin: "0 0 5px 0", fontWeight: "bold" }}
          >
            {formattedDate}
          </p>
          {payload.map((entry: any, index: number) => (
            <p
              key={`value-${index}`}
              style={{
                color: entry.color,
                margin: "3px 0",
                fontSize: "12px",
              }}
            >
              {isinToName[entry.dataKey] || entry.dataKey}:{" "}
              {formatItalianNumber(entry.value, 2)}
              {useYAxisPercentage ? "%" : ""}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getXAxisInterval = (dataLength: number) => {
    if (dataLength > 120) return Math.ceil(dataLength / 12); // Show approximately 12 labels max
    if (dataLength > 60) return 12; // Annual for 5+ years of data
    if (dataLength > 24) return 6; // Semi-annual for 2+ years
    if (dataLength > 12) return 3; // Quarterly for 1+ year
    return 1; // Monthly for less than a year
  };

  const CustomizedLegend = () => {
    return (
      <div className={styles.bottomLegendContainer}>
        {performanceProducts.map((isin, index) => (
          <div
            key={`legend-${isin}`}
            className={styles.legendItem}
            onClick={() => toggleProductSelection(isin)}
            style={{
              cursor: "pointer",
              opacity: selectedProducts.includes(isin) ? 1 : 0.5,
            }}
          >
            <div
              className={styles.legendDot}
              style={{ backgroundColor: lineColors[index % lineColors.length] }}
            />
            <span
              className={styles.legendCopy}
              style={{ color: colorsConfig.finecoBlack }}
            >
              {isinToName[isin] || isin}
            </span>
          </div>
        ))}
      </div>
    );
  };

  let percentageAdder = "";
  if (useYAxisPercentage) {
    percentageAdder = "%";
  }

  // Generate tick values that start from minValue and go to maxValue in tickStep increments
  const yAxisTicks = [];
  for (let i = minValue; i <= maxValue; i += tickStep) {
    yAxisTicks.push(i);
  }

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.chartContainer}>
        <ResponsiveContainer debounce={500}>
          <LineChart
            data={filteredData}
            margin={{ top: 10, right: 10, left: -15, bottom: 50 }}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.FinecoGrayLight1}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              dx={10}
              dy={5}
              angle={-25}
              textAnchor="end"
              height={40}
              interval={getXAxisInterval(filteredData.length)}
              tick={chartCopy}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.FinecoGrayLight1 }}
            />
            <YAxis
              tick={chartCopy}
              domain={[minValue, maxValue]}
              tickLine={false}
              ticks={yAxisTicks}
              tickFormatter={(value) =>
                `${Math.round(value)}${percentageAdder}`
              }
              axisLine={{ stroke: colorsConfig.FinecoGrayLight1 }}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Render a line for each product */}
            {performanceProducts.map(
              (isin, index) =>
                selectedProducts.includes(isin) && (
                  <Line
                    key={`line-${isin}`}
                    name={isinToName[isin] || isin}
                    type="monotone"
                    dataKey={isin}
                    stroke={lineColors[index % lineColors.length]}
                    dot={false}
                    strokeWidth={1.5}
                    strokeOpacity={1}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                ),
            )}
          </LineChart>
        </ResponsiveContainer>

        {/* Custom Legend at the bottom inside the chart container */}
        <CustomizedLegend />
      </div>
    </div>
  );
};

export default RendimentiChartCompare;
